import { Grid, IconButton, useEventCallback } from '@mui/material';
import { Fragment, memo, useState } from 'react';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { TypeFile } from 'practice/components/detail/form/accordion-attachments/form';
import { DocumentVM } from 'civil-status/view-models/step-form';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { HttpResponse } from 'shell/models/http-response';
import { AttachmentsData } from 'shell/models/act-wizard';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { ItemVM } from 'shell/view-models/item';
import { PmiInputFile } from 'shell/components/shared/file-input';
import { TYPE_DOCUMENT_PROCESSO_VERBALE } from 'civil-status/constants/type-document-processo-verbale';
import DeleteIcon from '@mui/icons-material/Delete';

interface FormProps {
  disabled?: boolean;
  onChange: (key: keyof DocumentVM, value: DocumentVM[keyof DocumentVM]) => void;
  onChanges: (values: DocumentVM) => void;
  values?: DocumentVM;
  idEvento?: number;
  errorFiles?: boolean;
}

const FormComponent = ({ disabled, onChange, values, idEvento }: FormProps): React.ReactElement => {
  const [errorToVisualize, setErrorToVisualize] = useState<{
    visibility?: boolean;
    title?: string;
    messages?: Array<ItemVM>;
  }>({
    visibility: false,
    title: '',
    messages: [],
  });
  const insertData = useHttpClient(`${__ACT_API_URL__}/allegato-atti/inserimento`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const handleAddFile = useEventCallback((file: File) => {
    const reader = new FileReader();
    reader.addEventListener('load', (event) => {
      const { result } = event.target || {};

      // inserire un record sulla tabella allegato
      const data = {
        idEvento,
        tipoAllegato: TYPE_DOCUMENT_PROCESSO_VERBALE,
        // descrizioneTipoAllegato: TODO mettere qualcosa fisso?
        nomeFile: file.name || undefined,
        oggettoAllegato: (result as string)?.split(',')[1],
      };

      (
        insertData({
          data,
        }) as ResponsePromise<HttpResponse<AttachmentsData>>
      ).then((response) => {
        toast.success('Operazione eseguita con successo');

        const fileNew: TypeFile = {
          ...file,
          raw: result as string,
          idAllegato: response?.data.payload.key?.idAllegato,
          name: file.name || '',
          tipoAllegato: response?.data.payload.tipoAllegato,
          codStato: response?.data.payload.codStato,
        };

        onChange('file', fileNew);
      });
    });

    reader.readAsDataURL(file);
  });

  const onVisibility = useEventCallback(() => {
    setErrorToVisualize({ visibility: false, title: '', messages: [] });
  });

  const deleteData = useHttpClient(``, {
    options: {
      method: 'DELETE',
      loading: true,
      errorCatching: true,
    },
  });

  const onTrashPress = useEventCallback(() => {
    if (!values?.file?.idAllegato) {
      return;
    }

    (
      deleteData({
        url: `${__ACT_API_URL__}/allegato-atti/cancellazione/${values?.file?.idAllegato}`,
      }) as unknown as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');

      onChange('file', undefined);
    });
  });

  return (
    <Grid container spacing={2} item>
      <Grid item xs={12}>
        La dimensione del file non deve superare 10 Mb; il nome del file non deve contenere spazi o
        caratteri speciali; sono ammesse le seguenti tipologie di file: PDF, JPG, TIF, P7M.
      </Grid>
      <Grid item xs={12}>
        <SearchMessages
          type={MessageType.Error}
          title={errorToVisualize.title || ''}
          messages={errorToVisualize.messages || []}
          visibility={errorToVisualize.visibility || false}
          onChangeVisibility={onVisibility}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <span>Atto cartaceo firmato *</span>
      </Grid>
      <Grid item xs={12} md={1}>
        <PmiInputFile
          file={values?.file}
          onChange={(file) => handleAddFile(file)}
          color="cdmBrandThird"
          showFilename={false}
          disabled={disabled || Boolean(values?.file?.idAllegato)}
          multiple={false}
        />
      </Grid>
      {values?.file?.raw ? (
        <Fragment>
          <Grid item xs={12} md={5}>
            <a download={values?.file?.name} href={values?.file?.raw}>
              {values?.file?.name}
            </a>
          </Grid>
          <Grid item xs={12} md={1}>
            <IconButton
              color="error"
              aria-label="eliminare documento allegato"
              component="span"
              onClick={() => onTrashPress()}
              disabled={disabled}
            >
              <DeleteIcon />
            </IconButton>
          </Grid>
        </Fragment>
      ) : undefined}
    </Grid>
  );
};

FormComponent.displayName = 'Form - accordion document emergency - form';

const Form = memo(FormComponent);

export default Form;
